import { z } from "zod";
import { lauteAnswerSchema } from "./laute-erkennen";
import { recommendationPlanFields, validateRecommendationPlan } from "./recommendation-contracts";
import { transkodierenAnswerSchema } from "./transkodieren-contracts";
import { einmaleinsRunSettingsSchema } from "./einmaleins-contracts";

export const adaptiveEinmaleinsConfigSchema = einmaleinsRunSettingsSchema.safeExtend({
  focusProfile: z.enum(["gentle", "balanced", "focused"]),
});
export type AdaptiveEinmaleinsConfig = z.infer<typeof adaptiveEinmaleinsConfigSchema>;
import type { ExerciseType } from "./exercise-types";
import { matheOperationSchema, matheZahlenraumSchema } from "./mathe-contracts";

export const recommendationInputSchema = z.object({
  ...recommendationPlanFields,
  learnerId: z.string().uuid(),
  contentPageId: z.string().uuid(),
  message: z.string().max(1_000).default(""),
}).superRefine(validateRecommendationPlan);
export type RecommendationInput = z.infer<typeof recommendationInputSchema>;

export const adaptiveMatheConfigSchema = z.object({
  operations: z.array(matheOperationSchema).min(1).max(4).refine((values) => new Set(values).size === values.length),
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  focusProfile: z.enum(["gentle", "balanced", "focused"]),
  range: matheZahlenraumSchema.default("0-20"),
});

const adaptiveBlitzlesenSyllableCountSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);

export const adaptiveBlitzlesenConfigSchema = z.object({
  syllableCounts: z.array(adaptiveBlitzlesenSyllableCountSchema).max(4).default([]),
  materialBankId: z.string().trim().min(1).max(80).optional(),
  groupIds: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
  wordCount: z.number().int().min(0).max(5_000),
  displayDurationMs: z.number().int().min(50).max(9_000),
  syllableColors: z.boolean(),
  focusProfile: z.enum(["gentle", "balanced", "focused"]),
}).superRefine((settings, context) => {
  if (settings.materialBankId) {
    if (settings.groupIds.length === 0) context.addIssue({ code: "custom", path: ["groupIds"], message: "Mindestens eine Materialgruppe muss ausgewählt sein." });
    if (new Set(settings.groupIds).size !== settings.groupIds.length) context.addIssue({ code: "custom", path: ["groupIds"], message: "Jede Materialgruppe darf nur einmal ausgewählt sein." });
  } else if (settings.syllableCounts.length === 0) {
    context.addIssue({ code: "custom", path: ["syllableCounts"], message: "Mindestens eine Silbenanzahl muss ausgewählt sein." });
  }
});

const adaptiveAssignmentConfigSchema = z.union([adaptiveMatheConfigSchema, adaptiveBlitzlesenConfigSchema, adaptiveEinmaleinsConfigSchema]);

const adaptiveAssignmentCreateInputBaseSchema = z.object({
  ...recommendationPlanFields,
  learnerId: z.string().uuid(),
  contentPageId: z.string().uuid(),
  title: z.string().trim().min(1).max(160),
  message: z.string().trim().max(1_000).default(""),
  exerciseType: z.enum(["mathe", "blitzlesen", "einmaleins"]).default("mathe"),
  config: adaptiveAssignmentConfigSchema,
});

export const adaptiveAssignmentCreateInputSchema = adaptiveAssignmentCreateInputBaseSchema.superRefine((value, context) => {
  validateRecommendationPlan(value, context);
  const schema = value.exerciseType === "einmaleins" ? adaptiveEinmaleinsConfigSchema : value.exerciseType === "blitzlesen" ? adaptiveBlitzlesenConfigSchema : adaptiveMatheConfigSchema;
  if (!schema.safeParse(value.config).success) {
    context.addIssue({ code: "custom", path: ["config"], message: "Die adaptive Konfiguration passt nicht zur Übung." });
  }
});

export const adaptiveAssignmentUpdateInputSchema = adaptiveAssignmentCreateInputBaseSchema.omit({ learnerId: true, contentPageId: true, exerciseType: true, frequency: true, weekStartsOn: true, recommendedMinutes: true }).partial().strict().refine((value) => Object.keys(value).length > 0);

export type AdaptiveMatheConfig = z.infer<typeof adaptiveMatheConfigSchema>;
export type AdaptiveBlitzlesenConfig = z.infer<typeof adaptiveBlitzlesenConfigSchema>;
export type AdaptiveAssignmentCreateInput = z.infer<typeof adaptiveAssignmentCreateInputSchema>;
export type AdaptiveAssignmentUpdateInput = z.infer<typeof adaptiveAssignmentUpdateInputSchema>;

export const bookmarkInputSchema = z.object({ contentPageId: z.string().uuid() });
export const runStartInputSchema = z.object({ contentPageId: z.string().uuid(), settings: z.unknown().optional(), recommendationId: z.string().uuid().optional() });

const quizAnswerSchema = z.object({
  type: z.literal("quiz"),
  questionId: z.string().min(1).max(240),
  optionIds: z.array(z.string().min(1).max(240)).max(8).refine((values) => new Set(values).size === values.length),
});
const lueckentextAnswerSchema = z.object({ type: z.literal("lueckentext"), gapId: z.string().min(1).max(120), value: z.string().max(240) });
const wallEventSchema = z.object({
  seq: z.number().int().min(1).max(100_000),
  wallId: z.string().min(1).max(240),
  cellKey: z.string().regex(/^r\d+c\d+$/),
  value: z.number().int().nullable(),
});
const rechenmauerAnswerSchema = z.object({
  type: z.literal("rechenmauer"),
  wallId: z.string().min(1).max(240),
  values: z.record(z.string(), z.number().int().nullable()),
  events: z.array(wallEventSchema).max(5_000),
  submitAttempts: z.number().int().min(1).max(100),
});
const blitzlesenAnswerSchema = z.object({
  type: z.literal("blitzlesen"),
  wordId: z.string().min(1).max(240),
  rating: z.enum(["correct", "uncertain", "incorrect"]),
  sequence: z.number().int().min(1).max(100_000),
  displayDurationMs: z.number().int().min(50).max(9_000).optional(),
});
const silbentrennungAnswerSchema = z.object({
  type: z.literal("silbentrennung"),
  itemId: z.string().min(1).max(240),
  stage: z.enum(["vowels", "splits"]),
  vowelSelections: z.array(z.number().int().min(0).max(119)).max(120),
  splits: z.array(z.number().int().min(1).max(119)).max(40),
}).superRefine((answer, context) => {
  if (new Set(answer.vowelSelections).size !== answer.vowelSelections.length) context.addIssue({ code: "custom", path: ["vowelSelections"], message: "Eine Vokalstelle darf nur einmal gewählt werden." });
  if (new Set(answer.splits).size !== answer.splits.length) context.addIssue({ code: "custom", path: ["splits"], message: "Eine Trennstelle darf nur einmal gesetzt werden." });
});
const reimeAnswerSchema = z.object({ type: z.literal("reime"), itemId: z.string().min(1).max(240), answer: z.boolean() });
const zeitformenAnswerSchema = z.object({ type: z.literal("zeitformen"), itemId: z.string().min(1).max(240), tense: z.enum(["praesens", "praeteritum", "perfekt", "plusquamperfekt", "futur1", "futur2"]) });
const zeitformenKonjugierenAnswerSchema = z.object({ type: z.literal("zeitformen-konjugieren"), itemId: z.string().min(1).max(240), values: z.array(z.string().max(160)).length(6) });
const spellingChoiceAnswerFields = { itemId: z.string().min(1).max(240), optionId: z.enum(["correct", "incorrect"]) };
const dehnungsHAnswerSchema = z.object({ type: z.literal("dehnungs-h"), ...spellingChoiceAnswerFields });
const kurzeLangeVokaleAnswerSchema = z.object({ type: z.literal("kurze-lange-vokale"), ...spellingChoiceAnswerFields });
const morphemStammAnswerSchema = z.object({
  type: z.literal("morphem-stamm"),
  itemId: z.string().min(1).max(240),
  selectedGrapheme: z.enum(["ä", "e", "äu", "eu", "Ä", "E", "Äu", "Eu"]),
  selectedRoot: z.string().trim().min(1).max(160),
  reassignedCount: z.number().int().min(0).max(100),
  decisionPath: z.array(z.enum(["grapheme", "root"])).min(2).max(202),
});
const morphemZerlegungAnswerSchema = z.object({
  type: z.literal("morphem-zerlegung"),
  itemId: z.string().min(1).max(240),
  cuts: z.tuple([z.number().int().min(0).max(160), z.number().int().min(0).max(160)]),
}).refine(({ cuts }) => cuts[0] < cuts[1], { path: ["cuts"], message: "Die beiden Trennstellen müssen verschieden und aufsteigend sein." });
const wordartSchema = z.enum(["Artikel", "Nomen", "Adjektiv", "Verb", "Adverb", "Pronomen", "Präposition", "Konjunktion", "Interjektion", "Numerale", "Partikel"]);
const wortartenAssignmentSchema = z.object({
  tokenId: z.string().min(1).max(120),
  chosenPos: wordartSchema.nullable(),
  assignmentOrder: z.number().int().min(1).max(10_000).nullable(),
  history: z.array(wordartSchema).max(100),
});
const wortartenAnswerSchema = z.object({ type: z.literal("wortarten"), sentenceId: z.string().min(1).max(240), assignments: z.array(wortartenAssignmentSchema).max(40) });
const grosskleinAnswerSchema = z.object({ type: z.literal("grossklein"), sentenceId: z.string().min(1).max(240), selectedTokenIds: z.array(z.string().min(1).max(120)).max(40) });
const satzgliederAnswerSchema = z.object({ type: z.literal("satzglieder"), sentenceId: z.string().min(1).max(240), assignments: z.array(z.object({ chunkId: z.string().min(1).max(120), chosenRole: z.enum(["Subjekt", "Prädikat", "Dativobjekt", "Akkusativobjekt", "Präpositionalobjekt", "Adverbiale Bestimmung"]).nullable() })).max(12) });
const matheAnswerSchema = z.object({
  type: z.literal("mathe"),
  itemId: z.string().min(1).max(240),
  value: z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER).nullable(),
  skipped: z.boolean().default(false),
  correctionCount: z.number().int().min(0).max(100).default(0),
  intermediateValues: z.array(z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER)).max(100).default([]),
}).superRefine((answer, context) => {
  if (!answer.skipped && answer.value === null) context.addIssue({ code: "custom", path: ["value"], message: "Eine Antwort oder ein übersprungener Durchgang ist erforderlich." });
});
const einmaleinsAnswerSchema = z.object({
  type: z.literal("einmaleins"),
  itemId: z.string().min(1).max(240),
  value: z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER).nullable(),
  skipped: z.boolean().default(false),
  correctionCount: z.number().int().min(0).max(100).default(0),
  intermediateValues: z.array(z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER)).max(100).default([]),
}).superRefine((answer, context) => {
  if (!answer.skipped && answer.value === null) context.addIssue({ code: "custom", path: ["value"], message: "Eine Antwort oder ein übersprungener Durchgang ist erforderlich." });
});
const stellenwertAdditionAnswerSchema = z.object({
  type: z.literal("stellenwert-addition"),
  itemId: z.string().min(1).max(240),
  writtenFirstAddend: z.number().int().min(10).max(99),
  writtenSecondAddend: z.number().int().min(10).max(99),
  firstIncorrectWrittenFirstAddend: z.number().int().min(10).max(99).nullable(),
  firstIncorrectWrittenSecondAddend: z.number().int().min(10).max(99).nullable(),
  transcriptionCorrectionCount: z.number().int().min(0).max(100),
  intermediateEntered: z.number().int().min(0).max(99),
  resultEntered: z.number().int().min(0).max(99),
}).superRefine((answer, context) => {
  const hasFirst = answer.firstIncorrectWrittenFirstAddend !== null;
  const hasSecond = answer.firstIncorrectWrittenSecondAddend !== null;
  if (hasFirst !== hasSecond) context.addIssue({ code: "custom", path: ["firstIncorrectWrittenFirstAddend"], message: "Der erste Übertragungsversuch muss vollständig sein." });
  if ((hasFirst && answer.transcriptionCorrectionCount === 0) || (!hasFirst && answer.transcriptionCorrectionCount > 0)) {
    context.addIssue({ code: "custom", path: ["transcriptionCorrectionCount"], message: "Korrekturanzahl und erster Fehlversuch passen nicht zusammen." });
  }
});
const rundenAnswerSchema = z.object({ type: z.literal("runden"), itemId: z.string().min(1).max(240), value: z.number().finite().min(0).max(1_000_000) });
const zahlenZerlegenAnswerSchema = z.object({ type: z.literal("zahlen-zerlegen"), itemId: z.string().min(1).max(240), selectedIndices: z.array(z.number().int().min(0).max(20)).min(3).max(8).refine((values) => new Set(values).size === values.length) });
const froschHupfenAnswerSchema = z.object({ type: z.literal("frosch-hupfen"), itemId: z.string().min(1).max(240), value: z.number().int().min(0).max(20) });
const waageAnswerSchema = z.object({ type: z.literal("waage"), itemId: z.string().min(1).max(240), answer: z.enum(["left", "equal", "right"]) });
const mengenMatchAnswerSchema = z.object({ type: z.literal("mengen-match"), itemId: z.string().min(1).max(240), value: z.number().int().min(1).max(20) });
const zaehlenTippenAnswerSchema = z.object({ type: z.literal("zaehlen-tippen"), itemId: z.string().min(1).max(240), tappedObjectIndices: z.array(z.number().int().min(0).max(19)).min(1).max(20).refine((values) => new Set(values).size === values.length), value: z.number().int().min(1).max(20) });
const sortierspielAnswerSchema = z.object({ type: z.literal("sortierspiel"), objectId: z.string().min(1).max(120), categoryId: z.string().min(1).max(120) });
const writtenArithmeticEventSchema = z.object({ seq: z.number().int().min(1).max(100_000), fieldKey: z.string().min(1).max(120), value: z.string().max(2) });
const writtenArithmeticAnswerSchema = z.object({
  type: z.literal("schriftlich-rechnen"),
  itemId: z.string().min(1).max(240),
  values: z.record(z.string().min(1).max(120), z.string().max(2)),
  events: z.array(writtenArithmeticEventSchema).max(5_000),
  submitAttempts: z.number().int().min(0).max(100),
  skipped: z.boolean().default(false),
});

export const exerciseAnswerRegistry = {
  quiz: quizAnswerSchema,
  lueckentext: lueckentextAnswerSchema,
  rechenmauer: rechenmauerAnswerSchema,
  mathe: matheAnswerSchema,
  einmaleins: einmaleinsAnswerSchema,
  "stellenwert-addition": stellenwertAdditionAnswerSchema,
  transkodieren: transkodierenAnswerSchema,
  runden: rundenAnswerSchema,
  "zahlen-zerlegen": zahlenZerlegenAnswerSchema,
  "frosch-hupfen": froschHupfenAnswerSchema,
  waage: waageAnswerSchema,
  "mengen-match": mengenMatchAnswerSchema,
  "zaehlen-tippen": zaehlenTippenAnswerSchema,
  sortierspiel: sortierspielAnswerSchema,
  "schriftlich-rechnen": writtenArithmeticAnswerSchema,
  blitzlesen: blitzlesenAnswerSchema,
  silbentrennung: silbentrennungAnswerSchema,
  "laute-erkennen": lauteAnswerSchema,
  reime: reimeAnswerSchema,
  zeitformen: zeitformenAnswerSchema,
  "zeitformen-konjugieren": zeitformenKonjugierenAnswerSchema,
  "dehnungs-h": dehnungsHAnswerSchema,
  "kurze-lange-vokale": kurzeLangeVokaleAnswerSchema,
  "morphem-stamm": morphemStammAnswerSchema,
  "morphem-zerlegung": morphemZerlegungAnswerSchema,
  wortarten: wortartenAnswerSchema,
  grossklein: grosskleinAnswerSchema,
  satzglieder: satzgliederAnswerSchema,
} satisfies Record<ExerciseType, z.ZodType>;

export const exerciseAnswerSchema = z.discriminatedUnion("type", [
  lauteAnswerSchema,
  quizAnswerSchema,
  lueckentextAnswerSchema,
  rechenmauerAnswerSchema,
  blitzlesenAnswerSchema,
  silbentrennungAnswerSchema,
  reimeAnswerSchema,
  zeitformenAnswerSchema,
  zeitformenKonjugierenAnswerSchema,
  dehnungsHAnswerSchema,
  kurzeLangeVokaleAnswerSchema,
  morphemStammAnswerSchema,
  morphemZerlegungAnswerSchema,
  wortartenAnswerSchema,
  grosskleinAnswerSchema,
  satzgliederAnswerSchema,
  matheAnswerSchema,
  einmaleinsAnswerSchema,
  stellenwertAdditionAnswerSchema,
  transkodierenAnswerSchema,
  rundenAnswerSchema,
  zahlenZerlegenAnswerSchema,
  froschHupfenAnswerSchema,
  waageAnswerSchema,
  mengenMatchAnswerSchema,
  zaehlenTippenAnswerSchema,
  sortierspielAnswerSchema,
  writtenArithmeticAnswerSchema,
]);

export type ExerciseAnswerInput = z.infer<typeof exerciseAnswerSchema>;
export type RunStartInput = z.infer<typeof runStartInputSchema>;
